// src/pages/Dashboard.tsx - FastMag Dashboard (Real DB Data)
import { supabase } from '@/lib/supabase';
import type { ActivityLog, Article, Profile } from '@/types';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowRight,
  CircleDollarSign,
  Clock,
  Package,
  TrendingUp,
  type LucideIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type ColorType = 'blue' | 'green' | 'orange' | 'purple'

interface StatsCardProps {
  title: string
  value: string | number
  icon: LucideIcon
  color: ColorType
  isLoading?: boolean
}

// ─── Stats Card ─────────────────────────────────────────────────
const StatsCard = ({ title, value, icon: Icon, color, isLoading }: StatsCardProps) => {
  const colorClasses: Record<ColorType, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
        <div className="h-8 bg-slate-200 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-slate-600 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-slate-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

// ─── Low Stock Alerts ───────────────────────────────────────────
interface LowStockAlertsProps {
  articles: Article[]
  isLoading: boolean
}

const LowStockAlerts = ({ articles, isLoading }: LowStockAlertsProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-slate-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">Scorte in Esaurimento</h3>
        {articles.length > 0 && (
          <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
            {articles.length}
          </span>
        )}
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-green-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Tutte le scorte sono a livello adeguato</p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.slice(0, 5).map((item) => (
            <div
              key={item.id}
              onClick={() => navigate('/articles/edit', { state: { article: item } })}
              className="border border-orange-200 bg-orange-50 rounded-lg p-3 hover:bg-orange-100 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <p className="font-medium text-slate-800 text-sm truncate">{item.codice_prodotto}</p>
                  </div>
                  {item.descrizione_prodotto && (
                    <p className="text-xs text-slate-600 truncate">{item.descrizione_prodotto}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-orange-700">{item.quantita_magazzino_attuale}</p>
                  <p className="text-xs text-slate-500">Min: {item.quantita_minima_magazzino}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {articles.length > 5 && (
        <button
          onClick={() => navigate('/articles')}
          className="w-full mt-4 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors touch-manipulation active:scale-95 flex items-center justify-center gap-2"
        >
          Vedi Tutti ({articles.length})
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ─── Recent Activity ────────────────────────────────────────────
interface LogWithUser extends ActivityLog {
  user?: Profile
}

interface RecentActivityProps {
  logs: LogWithUser[]
  isLoading: boolean
}

const actionCategoryLabels: Record<string, { label: string; style: string }> = {
  article: { label: 'Articolo', style: 'bg-blue-50 text-blue-700 border-blue-200' },
  category: { label: 'Categoria', style: 'bg-purple-50 text-purple-700 border-purple-200' },
  location: { label: 'Posizione', style: 'bg-green-50 text-green-700 border-green-200' },
  auth: { label: 'Auth', style: 'bg-slate-50 text-slate-700 border-slate-200' },
  system: { label: 'Sistema', style: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Adesso';
  if (diffMins < 60) return `${diffMins}m fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  if (diffDays < 7) return `${diffDays}g fa`;

  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getUserFullName = (user?: Profile): string => {
  if (!user) return 'Sconosciuto';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || 'Sconosciuto';
};

const RecentActivity = ({ logs, isLoading }: RecentActivityProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-slate-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">Attività Recenti</h3>

      {logs.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Nessuna attività registrata</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const catInfo = actionCategoryLabels[log.action_category] || {
              label: log.action_category,
              style: 'bg-slate-50 text-slate-700 border-slate-200'
            };

            return (
              <div
                key={log.id}
                className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className={`px-2 py-1 rounded text-xs font-bold border flex-shrink-0 ${catInfo.style}`}>
                    {catInfo.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{log.description}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      <span>{getUserFullName(log.user)}</span>
                      <span>•</span>
                      <span>{log.created_at ? formatTimestamp(log.created_at) : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        onClick={() => navigate('/logsview')}
        className="w-full mt-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-4 rounded-lg transition-colors touch-manipulation flex items-center justify-center gap-2"
      >
        Vedi Tutte le Attività
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────
export default function Dashboard() {
  // Fetch all articles (for stats + low stock)
  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ['dashboard-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('codice_prodotto');

      if (error) throw error;
      return data as Article[];
    }
  });

  // Fetch recent activity logs
  const { data: rawLogs = [], isLoading: loadingLogs } = useQuery({
    queryKey: ['dashboard-recent-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as ActivityLog[];
    }
  });

  // Fetch users for log display
  const { data: users = [] } = useQuery({
    queryKey: ['dashboard-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .order('email');

      if (error) throw error;
      return data as Profile[];
    }
  });

  // Compute stats from articles
  const totalArticles = articles.length;

  const lowStockArticles = articles.filter(
    a => (a.quantita_magazzino_attuale || 0) <= (a.quantita_minima_magazzino || 0)
  );

  const totalCostValue = articles.reduce((sum, a) => {
    const qty = a.quantita_magazzino_attuale || 0;
    const cost = typeof a.costo_acquisto === 'string'
      ? parseFloat(a.costo_acquisto) || 0
      : a.costo_acquisto || 0;
    return sum + (qty * cost);
  }, 0);

  const totalSellValue = articles.reduce((sum, a) => {
    const qty = a.quantita_magazzino_attuale || 0;
    const price = typeof a.prezzo_vendita === 'string'
      ? parseFloat(a.prezzo_vendita) || 0
      : a.prezzo_vendita || 0;
    return sum + (qty * price);
  }, 0);

  const formattedCostValue = `€ ${totalCostValue.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

  const formattedSellValue = `€ ${totalSellValue.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

  // Join logs with users
  const logs: LogWithUser[] = rawLogs.map(log => ({
    ...log,
    user: users.find(u => u.id === log.user_id)
  }));

  const isLoading = loadingArticles;

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Dashboard</h1>
          <p className="text-slate-600">Benvenuto in FastMag - Gestione rapida del tuo magazzino</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatsCard
            title="Articoli Totali"
            value={totalArticles}
            icon={Package}
            color="blue"
            isLoading={isLoading}
          />
          <StatsCard
            title="Scorte Basse"
            value={lowStockArticles.length}
            icon={AlertCircle}
            color="orange"
            isLoading={isLoading}
          />
          <StatsCard
            title="Valore (Costo)"
            value={formattedCostValue}
            icon={CircleDollarSign}
            color="green"
            isLoading={isLoading}
          />
          <StatsCard
            title="Valore (Vendita)"
            value={formattedSellValue}
            icon={TrendingUp}
            color="purple"
            isLoading={isLoading}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <LowStockAlerts articles={lowStockArticles} isLoading={loadingArticles} />
          <RecentActivity logs={logs} isLoading={loadingLogs} />
        </div>
      </div>
    </div>
  );
}